import { type CSSProperties, useEffect, useRef, useState } from "react";
import { useReducedMotion } from "../lib/useReducedMotion";

/**
 * CountFx, the patient-surface number reveal.
 *
 * Animates a number from 0 (or a previous value) to the target value
 * over `--motion-count-up` (~800ms) with an aggressive ease-out, so
 * the reader sees the final value within the first ~500ms and the
 * remaining time is a soft settle.
 *
 * Patient surfaces only. CFO/buyer dashboards (Stripe, Mercury, Brex,
 * Ramp, Vercel) snap their numeric headlines - count-up on those reads
 * as theatrical and slightly distrustful. Apple Health, Oura, Whoop,
 * Eight Sleep all do count up on personal-health metrics, which is
 * the precedent we're matching here.
 *
 * Fires once per metric per session, gated by a module-scoped Set
 * keyed on `metricId`. Subsequent renders (or route revisits) snap
 * to the value. Pass a fresh `metricId` to re-arm.
 *
 * `prefers-reduced-motion: reduce`: snaps to value, no RAF loop.
 *
 * Compositor: only runs while animating. No `will-change`. No layout
 * thrash; we render text content via a single state value and
 * `tabular-nums` keeps width stable.
 */

const seenMetrics = new Set<string>();

export interface CountFxProps {
  /**
   * The final value to animate to. The component formats this via
   * `format` and animates the integer underneath; non-integer targets
   * are interpolated and floored each frame so commas/decimals look
   * stable.
   */
  value: number;
  /**
   * Stable identifier for this metric. The first render with a given
   * id animates; later renders with the same id snap. Use something
   * like `'thrive-score'`, `'avg-sleep-hours-7d'`, etc.
   */
  metricId: string;
  /**
   * Format the in-flight value into the displayed string. Receives
   * the current interpolated number each frame. Should be stable
   * across renders (memoise upstream if it captures props).
   *
   * Default: `Math.round(n).toString()`.
   */
  format?: (n: number) => string;
  /**
   * Duration override in ms. Defaults to `--motion-count-up` (~800ms).
   */
  duration?: number;
  /**
   * Starting value for the animation. Defaults to 0. Pass a previous
   * value to animate between two states (e.g. score moved from 58 to
   * 62 after sync).
   */
  from?: number;
  /**
   * Optional className applied to the wrapping `<span>`. The wrapper
   * is `inline-block` with `tabular-nums` to keep digit widths stable.
   */
  className?: string;
  style?: CSSProperties;
}

const DEFAULT_FORMAT = (n: number) => Math.round(n).toString();

/**
 * Aggressive ease-out (matches `--ease-out-expo` cubic-bezier 0.16,1,0.3,1)
 * - front-loaded so the reader sees the final value within ~500ms of
 * an 800ms run, and the tail is soft settle.
 */
function easeOutExpo(t: number): number {
  return t === 1 ? 1 : 1 - 2 ** (-10 * t);
}

export function CountFx({
  value,
  metricId,
  format = DEFAULT_FORMAT,
  duration = 800,
  from = 0,
  className,
  style,
}: CountFxProps) {
  const reduced = useReducedMotion();
  const [display, setDisplay] = useState<number>(() => {
    // Already animated this metric in this session - snap.
    if (seenMetrics.has(metricId)) return value;
    // First render: start at `from` and animate in `useEffect`.
    return from;
  });
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    // If reduced motion, or we've already animated this metric, snap.
    if (reduced || seenMetrics.has(metricId)) {
      setDisplay(value);
      seenMetrics.add(metricId);
      return;
    }

    seenMetrics.add(metricId);
    const start = performance.now();
    const startValue = from;
    const delta = value - startValue;

    const tick = (now: number) => {
      const elapsed = now - start;
      const t = Math.min(1, elapsed / duration);
      const eased = easeOutExpo(t);
      setDisplay(startValue + delta * eased);
      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        rafRef.current = null;
      }
    };

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [value, metricId, reduced, duration, from]);

  return (
    <span className={className} style={{ fontVariantNumeric: "tabular-nums", ...style }}>
      {format(display)}
    </span>
  );
}

/**
 * Test-only: clear the seen-metrics set so each test starts fresh.
 * Production code should not call this.
 */
export function __resetCountFxSession() {
  seenMetrics.clear();
}
